import type { OnInit } from '@angular/core'

import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { MatIconModule } from '@angular/material/icon'
import { AlertComponentThemes } from './alert.constants'
import { AlertService } from './alert.service'
import { AlertCard, AlertCardInfo } from './alert.type'
import { slideInOutAnimation } from '../../animations'

@Component({
    selector: 'app-alert',
    templateUrl: './alert.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [slideInOutAnimation],
    imports: [MatIconModule],
    providers: [AlertService]
})
export class AlertComponent implements OnInit {
  public themes = AlertComponentThemes
  public timeouts: { id: string; timeout: ReturnType<typeof setTimeout> }[] = []
  public alerts = signal<AlertCard[]>([])

  private readonly alertService = inject(AlertService)
  private readonly destroyRef = inject(DestroyRef)

  public ngOnInit(): void {
    this.alertService.alertEmitter$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
        const alreadyExistingAlert = this.alerts().find((alert) => alert.id() === data.id)
        if (alreadyExistingAlert) {
          this.updateHandler(alreadyExistingAlert, data)
        } else {
          this.showHandler(data as AlertCardInfo)
        }
      })

    this.alertService.closeEmitter$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((id) => {
      const alert = this.alerts().find((alert) => alert.id() === id)
      if (!alert) return
      this.removeHandler(alert)
    })
  }


  private showHandler({ id, message, theme = 'info', timeout = 5_000, progress = null }: AlertCardInfo): void {
    const alert = {
      id: signal(id),
      message: signal(message),
      theme: signal(theme),
      timeout: signal(timeout),
      animationState: signal('enter'),
      progress: progress ? signal(progress) : null,
    }

    this.alerts.update((alerts) => [...alerts, alert])
    if (timeout) {
      this.timeouts.push({ id, timeout: setTimeout(() => this.removeHandler(alert), timeout) })
    }
  }

  public updateHandler(card: AlertCard, { message, theme, timeout, progress }: Partial<Omit<AlertCardInfo, 'id'>>): void {
    if (message) {
      card.message.set(message)
    }

    if (theme) {
      card.theme.set(theme)
    }

    if (timeout) {
      const oldTimeout = this.timeouts.find((timeout) => timeout.id === card.id())
      clearTimeout(oldTimeout?.timeout)
      card.timeout.set(timeout)
      this.timeouts.push({ id: card.id(), timeout: setTimeout(() => this.removeHandler(card), timeout) })
    }

    if (progress && card.progress) {
      if (progress === null) {
        card.progress = null
        return
      }

      card.progress.set(progress)
      return
    }

    if (progress && !card.progress) {
      card.progress = signal(progress)
    }
  }

  public animationHandler(alert: AlertCard): void {
    const states: { [key: string]: () => void } = {
      enter: () => alert.animationState.set('stable'),
      leave: () => this.removeHandler(alert),
    }

    const stateCallback = states[alert.animationState()]
    if (!stateCallback) {
      return
    }

    stateCallback()
  }

  public removeHandler(alert: AlertCard): void {
    if (alert.animationState() === 'enter' || alert.animationState() === 'stable') {
      alert.animationState.set('leave')
      return
    }

    this.alerts.update((alerts) => alerts.filter((a) => a.id() !== alert.id()))
  }

  public trackByFn(_: number, alert: AlertCard): string {
    return alert.id()
  }

  public resetHandler(): void {
    for (const timeout of this.timeouts) {
      clearTimeout(timeout.timeout)
    }

    for (const alert of this.alerts()) {
      alert.animationState.set('leave')
    }
  }
}
